import React, { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Paragraph } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { getNestedSubelementsByType, getPersonName, useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'

/**
 * ReviewCardBody
 *
 * Displays instructions for the user to review entered details. Prompts them to attach to tree, add
 * another household member, or close; under closeOnly it says the person is ready instead.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.person           SLS person object containing at least localizedName.
 * @param {string}         [props.splitScreenPartitionKey] PartitionKey of split screen image if active.
 * @param {boolean}        [props.closeOnly=false] Whether the footer offers Close alone.
 * @returns {JSX.Element}
 */
function ReviewCardBody({ person, splitScreenPartitionKey, closeOnly = false }) {
  const [t] = useTranslation()
  const { currentImageArk } = useSlsStateContext()
  const { setDisplayHighlights } = useSlsHighlightsContext()
  const { topMostPanel } = useOverlayContext()
  const topMostPanelId = useMemo(() => topMostPanel(), [topMostPanel])

  const personName = getPersonName({ person, justOneName: true })

  useEffect(() => {
    if (currentImageArk) {
      const fields = getNestedSubelementsByType(person, 'FIELD')
      const fieldIds = fields.map((field) => field.id)

      setDisplayHighlights({
        mainPartitionKey: currentImageArk,
        splitScreenPartitionKey,
        elementIds: fieldIds,
      })
    }
  }, [currentImageArk, splitScreenPartitionKey, topMostPanelId, setDisplayHighlights, person])

  return (
    <Paragraph size="sm">
      {closeOnly
        ? t('review-card.body.close-only', 'If everything looks correct, close to add { personName } to the group.', {
            personName,
          })
        : t(
            'review-card.body.household-actions',
            'If everything looks correct, you can choose to attach { personName } to the tree, add another person to this household, or close.',
            { personName }
          )}
    </Paragraph>
  )
}

ReviewCardBody.propTypes = {
  person: PropTypes.shape({
    localizedName: PropTypes.string,
  }).isRequired,
  splitScreenPartitionKey: PropTypes.string,
  closeOnly: PropTypes.bool,
}

ReviewCardBody.defaultProps = {
  closeOnly: false,
}

export default ReviewCardBody
